import { formChipTitle } from "@/shared/formChipTitle.ts";
import { useStreamedResponseText } from "@/shared/useStreamedResponseText.ts";
import { MessageCircle } from "lucide-react";
import { type ReactNode, useState } from "react";
import { warnUnsupportedValue } from "../../lib/error.ts";
import { cn } from "../../lib/utils.ts";
import { SlackIcon } from "../icons/SlackIcon.tsx";
import { TeamsIcon } from "../icons/TeamsIcon.tsx";
import { WhatsAppIcon } from "../icons/WhatsAppIcon.tsx";
import { formAnsweredLabel, isFormAnswered } from "../../types/artifacts.ts";
import type { ExternalChannel, UserResponseArtifact } from "../../types/artifacts.ts";
import { ActionBar } from "../common/ActionBar/ActionBar.tsx";
import { useMessageContext } from "../messages/MessageContext.tsx";
import { ReactionPicker } from "../messages/ReactionPicker.tsx";
import { ReactionBadge } from "../messages/ReactionBadge.tsx";
import { useAnchoredBiscuitSlot } from "../messages/relocatedBiscuitContext.tsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip.tsx";
import { Markdown } from "../markdown/Markdown.tsx";
import type { SendMessageFn } from "../messages/hooks/useMessageList.tsx";
import { ArtifactChip } from "./ArtifactChip.tsx";
import {
  FORM_ARTIFACT_ICON,
  FormArtifactLightbox,
  useOpenFormArtifact,
} from "./formArtifactSurface.tsx";

export const ChannelIcon = ({ channel }: { channel?: ExternalChannel }) => {
  if (!channel) return null;

  switch (channel) {
    case "textMessage":
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <MessageCircle className="size-3.5 stroke-2 -mt-0.5" />
          </TooltipTrigger>
          <TooltipContent>Sent via text</TooltipContent>
        </Tooltip>
      );
    case "slack":
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <SlackIcon className="size-3.5 -mt-0.5" />
          </TooltipTrigger>
          <TooltipContent>Sent via Slack</TooltipContent>
        </Tooltip>
      );
    case "whatsapp":
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <WhatsAppIcon className="size-3.5 -mt-0.5" />
          </TooltipTrigger>
          <TooltipContent>Sent via WhatsApp</TooltipContent>
        </Tooltip>
      );
    case "teams":
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <TeamsIcon className="size-3.5 -mt-0.5" />
          </TooltipTrigger>
          <TooltipContent>Sent via Teams</TooltipContent>
        </Tooltip>
      );
    default:
      warnUnsupportedValue(channel);
      return null;
  }
};

/**
 * A form as the thread shows it: a chip, whatever state it is in, the same as
 * every heavy artifact. The questions live on the surface that can show all of
 * them at once rather than stretched down the feed — the omnibox card is the
 * other way in, and dismissing that one leaves this chip rather than dropping a
 * full form into the thread.
 *
 * Its own component so the hooks below it — the message context, the lightbox's
 * URL state — run only for a reply that actually carries a form.
 */
const ThreadFormChip = ({
  artifact,
  threadId,
}: {
  artifact: UserResponseArtifact;
  threadId: number;
}) => {
  const { openForm, isLightboxOpen, setLightboxOpen } = useOpenFormArtifact(
    artifact,
    threadId,
    "inline",
  );
  const form = artifact.form;
  if (!form) return null;

  return (
    <>
      <ArtifactChip
        icon={FORM_ARTIFACT_ICON}
        title={formChipTitle(form)}
        statusLabel={formAnsweredLabel(form) ?? "Waiting for your answer"}
        // Answered is settled, so it dims like a sent draft; an open question
        // stays at full strength. A form answered by a prose reply is settled
        // too, even though it carries no values to show.
        isFinalStatus={isFormAnswered(form)}
        isCancelled={false}
        onOpen={openForm}
      />
      <FormArtifactLightbox
        artifact={artifact}
        threadId={threadId}
        open={isLightboxOpen}
        onOpenChange={setLightboxOpen}
      />
    </>
  );
};

export const ResponseArtifactItem = ({
  artifact,
  messageId,
  threadId,
  sendMessage,
  shouldStreamContent,
  hideFooter = false,
}: {
  artifact: UserResponseArtifact;
  messageId: number;
  threadId: number;
  sendMessage: SendMessageFn;
  shouldStreamContent: boolean;
  hideFooter?: boolean;
}) => {
  const { revealedResponse, hasResponse, isRevealing, isTextStillArriving, streamedThisSession } =
    useStreamedResponseText(artifact, { revealGate: shouldStreamContent });
  const { message, reactions, canReact } = useMessageContext();

  // The cards this turn produced, rendered between the answer and its footer:
  // the answer says what was drafted, the card is the thing to act on.
  //
  // Deliberately not gated on `ownsFooter`. A card has already been removed
  // from its own inline spot by the time it gets here, so anything that
  // suppresses it here loses it from the thread entirely — and `hideFooter` is
  // decided by a footer-adjacency pass that knows nothing about the fold.
  // Whether this message draws a bar is a separate question from whether it
  // shows its cards.
  const anchoredBiscuits = useAnchoredBiscuitSlot(messageId);

  // Exactly one component renders a message's footer: this one, unless the
  // wrapping `ArtifactGroup` showed breadcrumbs, in which case it renders the
  // bar itself and passes `hideFooter` down here.
  const ownsFooter = !hideFooter && hasResponse;
  // The footer shows on hover only, but it must stay up while either picker is
  // open (the add button in it, or the reaction pill that changes it): the
  // pointer is over the popover then, not the message.
  const [pickerOpen, setPickerOpen] = useState(false);
  const currentReaction = hasResponse ? reactions?.at(-1) : undefined;
  const canPick = canReact && hasResponse && !isTextStillArriving;
  // Once the reply carries the user's reaction, the reaction itself is how they
  // change it; the footer's add button is only for a reply with none yet.
  let reaction: ReactNode;
  if (currentReaction && canPick) {
    reaction = (
      <ReactionPicker
        threadId={threadId}
        messageId={messageId}
        currentEmoji={currentReaction}
        onOpenChange={setPickerOpen}
      />
    );
  } else if (currentReaction) {
    reaction = <ReactionBadge reactions={[currentReaction]} reactor="you" className="static" />;
  }
  const addReaction =
    canPick && !currentReaction ? (
      <ReactionPicker threadId={threadId} messageId={messageId} onOpenChange={setPickerOpen} />
    ) : undefined;

  return (
    <div
      // Fade the whole block in only when a reply genuinely arrives whole with no
      // reveal — an in-session reply that never streamed, e.g. one that finalized
      // within a debounce. Keyed on session-lifetime buffer presence, not the
      // per-mount `isRevealing`, so a reply that DID stream doesn't fade again
      // when its leaf later remounts and re-pop settled prose.
      className={cn(
        // Same 8px the steps eyebrow keeps above the reply, so the chips below
        // read as part of the same turn rather than a separate block.
        "w-full flex flex-col gap-2",
        shouldStreamContent && !streamedThisSession && "animate-in fade-in-0 duration-200",
      )}
    >
      {hasResponse && (
        <Markdown
          className="prose prose-zinc max-w-full text-sm @md:text-base text-foreground"
          isStreaming={isRevealing}
          // `isRevealing` latches for the life of the mount, so it can't also
          // stand for "text is still growing" — a reply that streamed would
          // never regain its copy buttons until the leaf remounted.
          isTextStillArriving={isTextStillArriving}
          // A streamed agent reply renders here rather than through
          // `MessageItemMarkdown`, so this is the path a one-shot `action:`
          // link in the reply actually takes.
          message={message}
        >
          {revealedResponse}
        </Markdown>
      )}
      {/* Chips sit half a step further from the prose than the stack's 8px —
          enough separation to read as cards, without the footer drifting too. */}
      {artifact.form && (
        <div className="mt-1">
          <ThreadFormChip artifact={artifact} threadId={threadId} />
        </div>
      )}
      {anchoredBiscuits && <div className="mt-1">{anchoredBiscuits}</div>}
      {(reaction || (addReaction && !ownsFooter)) && (
        <div className="flex items-center gap-1">
          {reaction}
          {/* The add button belongs to this reply, not to its footer. When
              another message draws the footer (the group above), it stays here
              instead, or the reply could never be reacted to. */}
          {!ownsFooter && addReaction}
        </div>
      )}
      {ownsFooter && (
        <ActionBar
          item={{
            content: artifact.response,
            date: artifact.createdAt,
            isUserMessage: false,
            messageId: messageId,
            threadId: threadId,
            isHeavy: artifact.isHeavy,
          }}
          sendMessage={sendMessage}
          hoverOnly={!pickerOpen}
          rightContent={<ChannelIcon channel={artifact.channel} />}
          leadingContent={addReaction}
        />
      )}
    </div>
  );
};
